type EditorBinding = {
  referenceEl: HTMLElement;
  observer: MutationObserver;
};

const bindings = new Map<HTMLElement, EditorBinding>();

function getEffectiveBackgroundColor(el: Element | null): string | null {
  while (el) {
    const style = getComputedStyle(el);
    const color = style.backgroundColor;
    const image = style.backgroundImage;

    // Si on trouve un dégradé
    if (image && image !== 'none' && image.includes('gradient')) {
      return image; // retourne le linear-gradient(...)
    }

    // Sinon, si on a une couleur opaque
    if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
      return color;
    }

    // eslint-disable-next-line no-param-reassign
    el = el.parentElement;
  }

  return null;
}

// eslint-disable-next-line import/prefer-default-export
export function useEditorBackground() {
  function setReferenceElement(referenceEl: HTMLElement, editorEl?: HTMLElement) {
    if (editorEl) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      registerEditor(editorEl, referenceEl);
    }
  }

  function registerEditor(editorEl: HTMLElement, referenceEl: HTMLElement) {
    // Nettoyer s'il y avait déjà un binding pour cet éditeur
    if (bindings.has(editorEl)) {
      // eslint-disable-next-line no-unused-expressions
      bindings.get(editorEl)?.observer.disconnect();
      bindings.delete(editorEl);
    }

    const observer = new MutationObserver(() => {
      const bg = getEffectiveBackgroundColor(referenceEl);
      if (bg) {
        if (bg.includes('gradient')) {
          // eslint-disable-next-line no-param-reassign
          editorEl.style.backgroundImage = bg;
          // eslint-disable-next-line no-param-reassign
          editorEl.style.backgroundColor = ''; // on supprime la couleur si elle était mise avant
        } else {
          // eslint-disable-next-line no-param-reassign
          editorEl.style.backgroundColor = bg;
          // eslint-disable-next-line no-param-reassign
          editorEl.style.backgroundImage = ''; // on supprime le dégradé s'il existait avant
        }
      }
    });

    observer.observe(referenceEl, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: false,
    });

    bindings.set(editorEl, { referenceEl, observer });
    const bg = getEffectiveBackgroundColor(referenceEl);
    if (bg) {
      if (bg.includes('gradient')) {
        // eslint-disable-next-line no-param-reassign
        editorEl.style.backgroundImage = bg;
        // eslint-disable-next-line no-param-reassign
        editorEl.style.backgroundColor = ''; // on supprime la couleur si elle était mise avant
      } else {
        // eslint-disable-next-line no-param-reassign
        editorEl.style.backgroundColor = bg;
        // eslint-disable-next-line no-param-reassign
        editorEl.style.backgroundImage = ''; // on supprime le dégradé s'il existait avant
      }
    }
  }

  function unregisterEditor(editorEl: HTMLElement) {
    const binding = bindings.get(editorEl);
    if (binding) {
      binding.observer.disconnect();
      bindings.delete(editorEl);
    }
  }

  function applyBackgroundReferenceFromSelector(selector: string, template: Document) {
    const elements: Element[] = [];

    // 1. Le sélecteur lui-même s'il correspond à un .spm_rich_editor
    const selectorElement = template.querySelector(selector);
    if (selectorElement && selectorElement.classList.contains('spm_rich_editor')) {
      elements.push(selectorElement);
    }

    // 2. Tous les enfants .spm_rich_editor
    const childElements = template.querySelectorAll(`${selector} .spm_rich_editor`);
    if (childElements?.length) {
      childElements.forEach((el) => elements.push(el));
    }

    // Appliquer à chaque élément trouvé
    elements.forEach((el: Element) => {
      if (el && typeof el === 'object' && 'style' in el && 'tagName' in el) {
        const referenceEl = el as HTMLElement;

        // Trouver l’éditeur déjà lié à ce referenceEl
        const resolvedEditor = [...bindings.entries()]
          .find(([_, binding]) => binding.referenceEl === referenceEl)?.[0];

        if (resolvedEditor) {
          registerEditor(resolvedEditor, referenceEl);
        } else {
          setReferenceElement(referenceEl);
        }
      }
    });
  }

  return {
    setReferenceElement,
    registerEditor,
    unregisterEditor,
    applyBackgroundReferenceFromSelector,
  };
}
